import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  footer?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  maxHeight?: string;
}

// Overlay and panel share one timing so the panel never vanishes before the backdrop
const MODAL_TRANSITION = { duration: 0.15, ease: 'easeOut' } as const;

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = 'md',
  footer,
  closeOnOverlayClick = true,
  maxHeight = '80vh',
}: ModalProps) {
  // Lock page scroll while open; the stable gutter keeps the scrollbar space so nothing shifts
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const hasScrollbar = window.innerWidth > root.clientWidth;
    const previousOverflow = root.style.overflow;
    const previousGutter = root.style.getPropertyValue('scrollbar-gutter');
    if (hasScrollbar) root.style.setProperty('scrollbar-gutter', 'stable');
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = previousOverflow;
      root.style.setProperty('scrollbar-gutter', previousGutter);
    };
  }, [isOpen]);

  const sizeClasses = {
    sm: 'max-w-sm lg:max-w-md',
    md: 'max-w-md lg:max-w-lg',
    lg: 'max-w-lg lg:max-w-xl xl:max-w-2xl',
    xl: 'max-w-xl lg:max-w-2xl xl:max-w-4xl',
    full: 'max-w-full lg:max-w-6xl xl:max-w-7xl',
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={MODAL_TRANSITION}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 common-modal-overlay"
          onClick={closeOnOverlayClick ? onClose : undefined}
        >
          <motion.div
            initial={{ scale: 0.97 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.97 }}
            transition={MODAL_TRANSITION}
            className={`bg-white rounded-lg shadow-xl w-full ${sizeClasses[size]}`}
            style={{ maxHeight }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 lg:p-5 xl:p-6 border-b border-gray-200">
              <div className="flex-1">
                <h2 className="text-lg lg:text-xl font-semibold text-gray-900">{title}</h2>
                {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 flex-shrink-0">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 lg:p-5 xl:p-6 overflow-y-auto" style={{ maxHeight: 'calc(' + maxHeight + ' - 140px)' }}>
              {children}
            </div>
            {footer && <div className="p-4 lg:p-5 xl:p-6 border-t border-gray-200 bg-gray-50">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
